import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@material-ui/core';
import { securityInsightsApiRef } from '../../api/apiRef';
import { QuiesceReason } from '../../api/types';
import {
  useExistingReasons,
  useTemplateReasons,
  useReasonCodes,
} from './useQuiesceDialogData';
import { CreateReasonForm } from './CreateReasonForm';

interface QuiesceDialogProps {
  open: boolean;
  tool: string;
  srcId: string;
  /** The finding's asset — shown for context; no longer the "attach to
   * existing reason" lookup key as of v3 (see `cveOrRule` below). */
  asset?: string;
  /** The finding's team — reasons are scoped to (team, cve_or_rule), so
   * this narrows the "attach to existing reason" lookup to the same team. */
  team?: string;
  /** The finding's CVE/rule — v3's reason-scoping key. Used to look up
   * existing active reasons for the "attach to an existing reason" path
   * (any asset sharing this CVE/rule, not just this finding's own asset)
   * and to filter the reason-code taxonomy to applicable codes. Omitted
   * entirely disables the attach path (falls back to always creating a new
   * reason) and blocks submission entirely once required. */
  cveOrRule?: string;
  onClose: () => void;
  onSuccess: () => void;
}

/** Mirrors security-pipeline's `quiesce.MaxQuiesceDuration` (90 days) — the
 * flat cap that replaced v1's per-severity caps (14/30/60/90). */
const MAX_DURATION_DAYS = 90;
const DEFAULT_DURATION_DAYS = 30;

/**
 * Dialog for quiescing (suppressing) one finding. Every quiesce is a
 * "reason record" with at least one linked finding (see security-pipeline's
 * docs/QUIESCE_REASONS_DESIGN.md) — this dialog either creates a brand new
 * reason covering just this finding, or attaches this finding to an
 * existing active reason already covering the same asset (e.g. a shared
 * vendor-image reason that already covers sibling CVEs). Errors from the
 * backend (e.g. duration bounds validation) are shown verbatim inline —
 * these carry actionable detail that a toast would truncate or the user
 * would miss.
 */
export const QuiesceDialog = ({
  open,
  tool,
  srcId,
  asset,
  team,
  cveOrRule,
  onClose,
  onSuccess,
}: QuiesceDialogProps) => {
  const api = useApi(securityInsightsApiRef);
  const trimmedCveOrRule = cveOrRule?.trim() ?? '';

  const existingReasons = useExistingReasons(open, trimmedCveOrRule, team, api);
  const templateReasons = useTemplateReasons(
    open,
    asset,
    team,
    trimmedCveOrRule,
    api,
  );
  const { reasonCodes, reasonCodesLoaded } = useReasonCodes(open, api);

  const [mode, setMode] = useState<'create' | 'attach'>('create');
  const [selectedReasonId, setSelectedReasonId] = useState<number | ''>('');

  const [reasonCode, setReasonCode] = useState('');
  const [copyFromReasonId, setCopyFromReasonId] = useState<number | ''>('');
  const [copiedReasonCodeInapplicable, setCopiedReasonCodeInapplicable] =
    useState(false);
  const [description, setDescription] = useState('');
  const [compensatingControls, setCompensatingControls] = useState('');
  const [durationDaysInput, setDurationDaysInput] = useState(
    String(DEFAULT_DURATION_DAYS),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existingReasons.length === 0) {
      setMode('create');
      setSelectedReasonId('');
    }
  }, [existingReasons]);

  // Applicable reason codes for this finding — codes with no
  // `applies_to_pattern` always apply; codes with one only apply if it
  // matches `cveOrRule`. Mirrors security-pipeline's own
  // ReasonCodeAppliesTo check — a UX nicety to avoid a round-trip 400 for an
  // obviously-inapplicable code, not a substitute for the server-side check.
  const applicableReasonCodes = reasonCodes.filter(rc => {
    if (!rc.applies_to_pattern) return true;
    try {
      return new RegExp(rc.applies_to_pattern).test(trimmedCveOrRule);
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (
      reasonCode &&
      !applicableReasonCodes.some(rc => rc.code === reasonCode)
    ) {
      setReasonCode('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicableReasonCodes]);

  // No approved reason code exists for this issue yet. The Security
  // Platform team is still defining, per issue type, which reason codes
  // developers are allowed to use — until this finding's type has one, it
  // simply can't be quiesced, so the rest of the create-mode form is
  // pointless to show. Only evaluated once the taxonomy fetch has actually
  // completed, so this doesn't flash true during the initial load.
  const noReasonCodesAvailable =
    Boolean(trimmedCveOrRule) &&
    reasonCodesLoaded &&
    applicableReasonCodes.length === 0;

  const durationDays = Number(durationDaysInput);

  const reset = () => {
    setMode('create');
    setSelectedReasonId('');
    setReasonCode('');
    setDescription('');
    setCompensatingControls('');
    setDurationDaysInput(String(DEFAULT_DURATION_DAYS));
    setCopyFromReasonId('');
    setCopiedReasonCodeInapplicable(false);
    setError(null);
  };

  // Prefills description/compensating-controls/reason-code from an existing
  // reason on this asset — but never the duration, and never a reason code
  // that doesn't apply to this CVE. This still results in a distinct new
  // reason the user submits (and can edit) themselves, not a silent clone —
  // gating rules for the new issue are re-checked, not inherited.
  const handleCopyFrom = (id: number | '') => {
    setCopyFromReasonId(id);
    if (id === '') {
      setCopiedReasonCodeInapplicable(false);
      return;
    }
    const template = templateReasons.find(r => r.id === id);
    if (!template) return;
    setDescription(template.description);
    setCompensatingControls(template.compensating_controls);
    const codeApplies = applicableReasonCodes.some(
      rc => rc.code === template.reason_code,
    );
    if (codeApplies) {
      setReasonCode(template.reason_code);
      setCopiedReasonCodeInapplicable(false);
    } else {
      setReasonCode('');
      setCopiedReasonCodeInapplicable(true);
    }
  };

  const selectedTemplate: QuiesceReason | null =
    copyFromReasonId === ''
      ? null
      : templateReasons.find(r => r.id === copyFromReasonId) ?? null;

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'attach' && selectedReasonId !== '') {
        await api.attachFindings(selectedReasonId, [
          { tool, src_id: srcId },
        ]);
      } else {
        await api.createQuiesceReason({
          category: 'cant_fix',
          reasonCode,
          description,
          compensatingControls,
          durationDays,
          findings: [{ tool, src_id: srcId }],
        });
      }
      reset();
      onSuccess();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    mode === 'attach'
      ? selectedReasonId !== ''
      : Boolean(trimmedCveOrRule) &&
        Boolean(reasonCode) &&
        Boolean(description.trim()) &&
        Boolean(compensatingControls.trim()) &&
        durationDays > 0 &&
        durationDays <= MAX_DURATION_DAYS;

  // The dialog's main body is one of four mutually-exclusive states —
  // built as a flat if/else chain (rather than a nested ternary in JSX,
  // which the lint rule against nesting ternaries would reject) assigned
  // to a single variable rendered below.
  let mainBody: JSX.Element;
  if (mode === 'attach' && existingReasons.length > 0) {
    mainBody = (
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel>Existing reason</InputLabel>
        <Select
          value={selectedReasonId}
          onChange={e => setSelectedReasonId(e.target.value as number)}
          label="Existing reason"
          disabled={submitting}
        >
          {existingReasons.map(r => (
            <MenuItem key={r.id} value={r.id}>
              {r.description} (expires {r.expires_at.slice(0, 10)})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  } else if (!trimmedCveOrRule) {
    mainBody = (
      <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
        This finding has no CVE/rule — a quiesce reason cannot be created for
        it.
      </Typography>
    );
  } else if (noReasonCodesAvailable) {
    mainBody = (
      <Typography variant="body2" style={{ marginTop: 8 }}>
        This issue type doesn't have an approved quiesce reason yet, so it
        can't be quiesced right now. The Security Platform team is working
        with leadership to define the allowed reason codes per issue type —
        reach out in <strong>#security-public</strong> if you believe this
        issue should be quiescable.
      </Typography>
    );
  } else {
    mainBody = (
      <CreateReasonForm
        templateReasons={templateReasons}
        copyFromReasonId={copyFromReasonId}
        onCopyFrom={handleCopyFrom}
        selectedTemplate={selectedTemplate}
        copiedReasonCodeInapplicable={copiedReasonCodeInapplicable}
        applicableReasonCodes={applicableReasonCodes}
        reasonCode={reasonCode}
        onReasonCodeChange={code => {
          setReasonCode(code);
          setCopiedReasonCodeInapplicable(false);
        }}
        description={description}
        onDescriptionChange={setDescription}
        compensatingControls={compensatingControls}
        onCompensatingControlsChange={setCompensatingControls}
        durationDaysInput={durationDaysInput}
        onDurationDaysInputChange={setDurationDaysInput}
        maxDurationDays={MAX_DURATION_DAYS}
        submitting={submitting}
      />
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Quiesce finding</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {tool} / {srcId}
          {asset ? ` — ${asset}` : ''}
        </Typography>

        {existingReasons.length > 0 && (
          <RadioGroup
            value={mode}
            onChange={e => setMode(e.target.value as 'create' | 'attach')}
            row
            style={{ marginBottom: 8 }}
          >
            <FormControlLabel
              value="create"
              control={<Radio size="small" />}
              label="Create new reason"
              disabled={submitting}
            />
            <FormControlLabel
              value="attach"
              control={<Radio size="small" />}
              label="Attach to existing reason for this issue"
              disabled={submitting}
            />
          </RadioGroup>
        )}

        {mainBody}

        {error && (
          <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!canSubmit || submitting}
        >
          {submitting ? 'Quiescing...' : 'Quiesce'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
